import styled from '@emotion/styled';
import {
  ChangeEventHandler,
  HTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
} from 'react';

// Based on https://jacobruiz.com/blog/2021/2/11/how-to-transition-placeholder-text-into-a-label-in-react-floating-label-inputs

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  &:focus-within label {
    transform: translate(0, 1px) scale(0.6);
    color: ${({ theme }) => theme.colors.blueDark100};
  }
`;

const Input = styled.input`
  height: 46px;
  border-radius: 0px;
  border: none;
  background-color: transparent;
  border-bottom: 1px solid ${({ theme }) => theme.colors.blueDark40};
  padding: 16px 0px 4px 0;
  ${({ theme }) => theme.textStyles.labelXl};
  outline: none;
  box-shadow: none;
  margin-bottom: 16px; // Space for the error message

  &:focus {
    border-bottom: 2px solid ${({ theme }) => theme.colors.blueDark100};
    padding-bottom: 3px; // Don't shift the content when changing the border width
    color: ${({ theme }) => theme.colors.blueDark100};
  }

  &[aria-invalid='true'],
  &[aria-invalid='true']:focus {
    border-color: ${({ theme }) => theme.colors.brandCoral100};
  }
`;

const Label = styled('label', {
  shouldForwardProp: (prop) => prop !== 'filled',
})<{ filled: boolean }>`
  position: absolute;
  pointer-events: none;
  transform: translate(0, 16px) scale(1);
  transform-origin: top left;
  transition: 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
  color: ${({ theme }) => theme.colors.blueDark70};
  ${({ theme }) => theme.textStyles.labelXl};
  left: 0px;

  ${({ filled }) => (filled ? 'transform: translate(0, 1px) scale(0.6);' : '')}
`;

const Error = styled.div`
  position: absolute;
  left: 0;
  top: 50px;
  ${({ theme }) => theme.textStyles.labelS};
  color: ${({ theme }) => theme.colors.brandCoral100};
`;

interface Props extends HTMLAttributes<HTMLDivElement> {
  label: string;
  name: string;
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  error?: string | JSX.Element;
  type?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  labelProps?: LabelHTMLAttributes<HTMLLabelElement>;
}

export default function FloatingLabelInput({
  inputProps,
  labelProps,
  label,
  type,
  name,
  onChange,
  value,
  error,
  ...rest
}: Props) {
  const errorId = error ? `error-${name}` : undefined;
  return (
    <Container {...rest}>
      <Input
        type={type}
        name={name}
        id={name}
        value={value}
        onChange={onChange}
        aria-invalid={!!error}
        aria-describedby={errorId}
        {...inputProps}
      />
      <Label filled={!!value} htmlFor={name} {...labelProps}>
        {label}
      </Label>
      {error && <Error id={errorId}>{error}</Error>}
    </Container>
  );
}
